import { Injectable } from '@angular/core';
import { of } from 'rxjs';
import { map, mergeMap, catchError, concatMap, withLatestFrom } from 'rxjs/operators';

import { createEffect, ofType, Actions } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { RouterReducerState } from '@ngrx/router-store';

import { ReferenceService, RouterStateUrl } from '@sd-portal/shared/util';

import * as actions from './aircraft-event.actions';
import { State } from '..';
import { AircraftEventAuditService } from '../../aircraft-event-audit.service';

@Injectable()
export class AircraftEventEffects {
  constructor(
    private readonly aircraftEventAuditService: AircraftEventAuditService,
    private readonly referenceService: ReferenceService,
    private readonly actions$: Actions,
    private readonly store: Store<{
      router: RouterReducerState<RouterStateUrl>;
      sharedAudit: State;
    }>
  ) {}

  // Skeleton
  loadAircraftEventSkeletons$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadAircraftEventSkeletons),
      mergeMap(() =>
        this.aircraftEventAuditService.getAircraftEventSkeletons().pipe(
          map(payload => actions.AircraftEventSkeletonsLoaded({ payload })),
          catchError(err => of(actions.AircraftEventSkeletonsLoadError(err)))
        )
      )
    )
  );

  // // AircraftEvent
  loadAircraftEventAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadAircraftEventAudit),
      mergeMap(action =>
        this.aircraftEventAuditService.getAircraftEventAudits(action.aircraftEventId).pipe(
          map(audits =>
            actions.AircraftEventAuditLoaded({
              aircraftEventId: action.aircraftEventId,
              audits
            })
          ),
          catchError(err => of(actions.AircraftEventAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageAircraftEventAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageAircraftEventAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.aircraftEvent.aircraftEventAudit.lastAuditLogId;
        }
        return this.aircraftEventAuditService
          .getAircraftEventAudits(action.aircraftEventId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits =>
              actions.AircraftEventAuditLoaded({
                aircraftEventId: action.aircraftEventId,
                audits
              })
            ),
            catchError(err => of(actions.AircraftEventAuditLoadError(err)))
          );
      })
    )
  );

  // AircraftEventBusinessPerson
  loadAircraftEventBusinessPersonAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadAircraftEventBusinessPersonAudit),
      mergeMap(action =>
        this.aircraftEventAuditService
          .getAircraftEventBusinessPersonAudits(action.aircraftEventBusinessPersonId)
          .pipe(
            map(audits =>
              actions.AircraftEventBusinessPersonAuditLoaded({
                aircraftEventBusinessPersonId: action.aircraftEventBusinessPersonId,
                audits
              })
            ),
            catchError(err => of(actions.AircraftEventBusinessPersonAuditLoadError(err)))
          )
      )
    )
  );

  loadNextPageAircraftEventBusinessPersonAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageAircraftEventBusinessPersonAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId =
            state.sharedAudit.aircraftEvent.aircraftEventBusinessPersonAudit.lastAuditLogId;
        }
        return this.aircraftEventAuditService
          .getAircraftEventBusinessPersonAudits(
            action.aircraftEventBusinessPersonId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.AircraftEventBusinessPersonAuditLoaded({
                aircraftEventBusinessPersonId: action.aircraftEventBusinessPersonId,
                audits
              })
            ),
            catchError(err => of(actions.AircraftEventBusinessPersonAuditLoadError(err)))
          );
      })
    )
  );

  // // ScheduledEvent
  loadScheduledEventAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadScheduledEventAudit),
      mergeMap(action =>
        this.aircraftEventAuditService.getScheduledEventAudits(action.scheduledEventId).pipe(
          map(audits =>
            actions.ScheduledEventAuditLoaded({
              scheduledEventId: action.scheduledEventId,
              audits
            })
          ),
          catchError(err => of(actions.ScheduledEventAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageScheduledEventAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageScheduledEventAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.aircraftEvent.scheduledEventAudit.lastAuditLogId;
        }

        return this.aircraftEventAuditService
          .getScheduledEventAudits(
            action.scheduledEventId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.ScheduledEventAuditLoaded({
                scheduledEventId: action.scheduledEventId,
                audits
              })
            ),
            catchError(err => of(actions.ScheduledEventAuditLoadError(err)))
          );
      })
    )
  );

  // // AircraftEvent Types
  loadAircraftEventTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadAircraftEventTypes),
      mergeMap(() =>
        this.referenceService.getReferenceItems('AircraftEventType').pipe(
          map(payload => actions.AircraftEventTypesLoaded({ payload })),
          catchError(err => of(actions.AircraftEventTypesLoadError(err)))
        )
      )
    )
  );

  // // FlightBusinessPerson Types
  loadFligtBusinessPersonTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadFlightBusinessPersonTypes),
      mergeMap(() =>
        this.referenceService.getReferenceItems('FlightBusinessPersonType').pipe(
          map(payload => actions.FlightBusinessPersonTypesLoaded({ payload })),
          catchError(err => of(actions.FlightBusinessPersonTypesLoadError(err)))
        )
      )
    )
  );
}
